/**
 * Learner Creation Form Component
 *
 * Reference implementation following UI Standards (see /UI_STANDARDS.md)
 *
 * Standards Applied:
 * - Stack layout with spacing={3} for form fields
 * - Responsive Stack direction={{ xs: 'column', md: 'row' }}
 * - Consistent spacing: mb: 3 for sections, p: 4 for Paper
 * - Breadcrumb navigation for nested pages
 * - Page header with h4 title + body2 description
 * - Success/Error alerts with mb: 3
 * - Client-side validation with real-time error clearing
 * - Loading states on buttons and form fields
 * - FastAPI error handling (string and array formats)
 * - Auto-redirect after successful creation
 * - Proper TypeScript types for all state
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LearnerCreate } from '../../types/learner';

const LearnerForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<LearnerCreate>({
    user_id: 0,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    student_id: '',
    grade_level: 0,
    date_of_birth: '',
    ieb_exam_number: '',
    school_id: 0,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.user_id || formData.user_id === 0) {
      errors.user_id = 'User ID is required';
    }

    if (!formData.first_name || formData.first_name.trim() === '') {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name || formData.last_name.trim() === '') {
      errors.last_name = 'Last name is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.student_id || formData.student_id.trim() === '') {
      errors.student_id = 'Student ID is required';
    }

    if (!formData.grade_level || formData.grade_level === 0) {
      errors.grade_level = 'Grade level is required';
    } else if (formData.grade_level < 1 || formData.grade_level > 12) {
      errors.grade_level = 'Grade level must be between 1 and 12';
    }

    if (!formData.date_of_birth || formData.date_of_birth.trim() === '') {
      errors.date_of_birth = 'Date of birth is required';
    }

    if (!formData.school_id || formData.school_id === 0) {
      errors.school_id = 'School ID is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Convert empty strings to null for optional fields
      const payload = {
        ...formData,
        phone: formData.phone || null,
        ieb_exam_number: formData.ieb_exam_number || null,
      };

      await axios.post('/api/learners/', payload);

      setSuccess(true);

      // Redirect to learners list after 1.5 seconds
      setTimeout(() => {
        navigate('/learners');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating learner:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          // Handle FastAPI validation errors
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to create learner. Please check your input.');
        }
      } else {
        setError('Failed to create learner. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/learners');
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/learners')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Learners
        </MuiLink>
        <Typography color="text.primary">New Learner</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Create New Learner
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new learner (student)
        </Typography>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Learner created successfully! Redirecting to learners list...
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Row 1: First Name and Last Name */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                error={!!validationErrors.first_name}
                helperText={validationErrors.first_name}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                error={!!validationErrors.last_name}
                helperText={validationErrors.last_name}
                disabled={loading}
              />
            </Stack>

            {/* Row 2: Email and Phone */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional"
              />
            </Stack>

            {/* Row 3: Student ID and Grade Level */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Student ID"
                name="student_id"
                value={formData.student_id}
                onChange={handleChange}
                error={!!validationErrors.student_id}
                helperText={validationErrors.student_id}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                type="number"
                label="Grade Level"
                name="grade_level"
                value={formData.grade_level || ''}
                onChange={handleChange}
                error={!!validationErrors.grade_level}
                helperText={validationErrors.grade_level || 'Enter grade 1-12'}
                disabled={loading}
                inputProps={{ min: 1, max: 12 }}
              />
            </Stack>

            {/* Row 4: Date of Birth and IEB Exam Number */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date of Birth"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                error={!!validationErrors.date_of_birth}
                helperText={validationErrors.date_of_birth}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="IEB Exam Number"
                name="ieb_exam_number"
                value={formData.ieb_exam_number || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - For IEB registered students"
              />
            </Stack>

            {/* Row 5: User ID and School ID */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                type="number"
                label="User ID"
                name="user_id"
                value={formData.user_id || ''}
                onChange={handleChange}
                error={!!validationErrors.user_id}
                helperText={validationErrors.user_id}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                type="number"
                label="School ID"
                name="school_id"
                value={formData.school_id || ''}
                onChange={handleChange}
                error={!!validationErrors.school_id}
                helperText={validationErrors.school_id}
                disabled={loading}
              />
            </Stack>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Learner'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default LearnerForm;
